import {
  CalendarDays,
  BookOpenText,
  Camera,
  AlertTriangle,
  CheckCircle2,
  Wifi,
  BatteryFull,
  Signal,
  ArrowUpRight,
  Landmark,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
 * CSS-only product mock-ups with fictional data. No screenshots, so nothing
 * from a real workspace ever lands on the public landing page.
 * ------------------------------------------------------------------------- */

const FRAME = "border border-[rgba(11,42,74,0.14)] bg-white shadow-[var(--shadow-lift)]";

/** Phone — the Site Supervisor view (schedule, diary, rectifications). */
export const PhoneMockup = ({ className }) => (
  <div
    className={cn("relative w-[236px] sm:w-[224px] lg:w-[236px] overflow-hidden rounded-[30px] p-2", FRAME, className)}
    role="img"
    aria-label="BuiltOS mobile Site Supervisor view showing today's tasks, a site diary entry and open rectifications"
    data-testid="landing-phone-mockup"
  >
    <div className="relative overflow-hidden rounded-[22px] bg-background">
      {/* status bar */}
      <div className="flex items-center justify-between px-4 pt-2 text-[10px] font-medium text-foreground/70">
        <span className="font-mono-nums">7:42</span>
        <span className="absolute left-1/2 top-1.5 h-4 w-20 -translate-x-1/2 rounded-full bg-foreground/[0.08]" aria-hidden="true" />
        <span className="flex items-center gap-1">
          <Signal className="h-3 w-3" />
          <Wifi className="h-3 w-3" />
          <BatteryFull className="h-3 w-3" />
        </span>
      </div>

      {/* header */}
      <div className="px-4 pb-2 pt-3">
        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Site Supervisor</p>
        <div className="mt-0.5 flex items-center justify-between">
          <p className="font-heading text-[15px] font-semibold text-foreground">Hawthorn Reno</p>
          <span className="rounded-full bg-[hsl(var(--chart-received)/0.12)] px-2 py-0.5 text-[9px] font-semibold text-[hsl(var(--chart-received))]">
            On site
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground">Fixing stage · day 41 of 96</p>
      </div>

      {/* today */}
      <div className="mx-3 rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-card)]">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-foreground">Today</p>
          <span className="text-[9px] text-muted-foreground">3 tasks</span>
        </div>
        <div className="mt-2 space-y-1.5">
          {[
            ["Plasterboard delivery", "7:30", true],
            ["Electrical rough-in check", "10:00", false],
            ["Client walkthrough", "15:30", false],
          ].map(([t, at, done]) => (
            <div key={t} className="flex items-center gap-2">
              <span className={cn("h-3.5 w-3.5 rounded-full border", done ? "border-[hsl(var(--chart-received))] bg-[hsl(var(--chart-received))]" : "border-border bg-white")}>
                {done && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
              </span>
              <span className={cn("flex-1 text-[10px]", done ? "text-muted-foreground line-through" : "text-foreground")}>{t}</span>
              <span className="font-mono-nums text-[9px] text-muted-foreground">{at}</span>
            </div>
          ))}
        </div>
      </div>

      {/* diary */}
      <div className="mx-3 mt-2 rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-card)]">
        <p className="text-[10px] font-semibold text-foreground">Site diary</p>
        <p className="mt-1 text-[10px] leading-4 text-muted-foreground">
          Sheeting to ground floor complete. Sparky on site 2 hrs. Wind 25 km/h — no crane lift.
        </p>
        <div className="mt-2 flex gap-1.5">
          {["bg-[hsl(var(--chart-expenses)/0.35)]", "bg-[hsl(var(--chart-net)/0.35)]", "bg-[hsl(var(--chart-received)/0.35)]"].map((c) => (
            <span key={c} className={cn("h-8 flex-1 rounded-md", c)} aria-hidden="true" />
          ))}
        </div>
      </div>

      {/* rectifications */}
      <div className="mx-3 mb-14 mt-2 rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-card)]">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-foreground">Rectifications</p>
          <span className="rounded-full bg-amber-400/90 px-1.5 text-[9px] font-semibold text-stone-950">2 open</span>
        </div>
        <div className="mt-1.5 space-y-1">
          <p className="flex items-center gap-1.5 text-[10px] text-foreground">
            <AlertTriangle className="h-3 w-3 text-amber-500" /> Cornice gap — bed 2
          </p>
          <p className="flex items-center gap-1.5 text-[10px] text-foreground">
            <AlertTriangle className="h-3 w-3 text-amber-500" /> Downpipe bracket loose
          </p>
        </div>
      </div>

      {/* bottom nav */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-around border-t border-border bg-card/95 px-2 pb-3 pt-2 backdrop-blur">
        {[
          [CalendarDays, "Schedule", true],
          [BookOpenText, "Diary", false],
          [Camera, "Photos", false],
          [AlertTriangle, "Issues", false],
        ].map(([Icon, label, active]) => (
          <span key={label} className={cn("flex flex-col items-center gap-0.5 text-[8px] font-medium", active ? "text-primary" : "text-muted-foreground")}>
            <Icon className="h-3.5 w-3.5" />
            {label}
          </span>
        ))}
      </div>
    </div>
  </div>
);

/** Laptop — the CFO / admin dashboard (margin, committed costs, Xero matching). */
export const LaptopMockup = ({ className }) => {
  const bars = [
    [42, 30, 58],
    [55, 38, 66],
    [48, 44, 60],
    [70, 52, 74],
    [62, 58, 80],
    [78, 64, 88],
  ];
  return (
    <div
      className={cn("w-full max-w-[680px]", className)}
      role="img"
      aria-label="BuiltOS desktop finance dashboard showing margin, committed costs, Xero matching, a cashflow chart and recent cost matches"
      data-testid="landing-laptop-mockup"
    >
      <div className={cn("overflow-hidden rounded-[18px] p-1.5", FRAME)}>
        <div className="overflow-hidden rounded-[13px] bg-background">
          {/* top bar */}
          <div className="flex h-8 items-center gap-2 border-b border-border bg-card px-3">
            <span className="flex gap-1" aria-hidden="true">
              <span className="h-2 w-2 rounded-full bg-border" />
              <span className="h-2 w-2 rounded-full bg-border" />
              <span className="h-2 w-2 rounded-full bg-border" />
            </span>
            <span className="ml-2 font-heading text-[10px] font-bold tracking-[-0.02em] text-primary">
              Built<span className="text-[hsl(var(--chart-net))]">OS</span>
            </span>
            <span className="ml-3 text-[9px] text-muted-foreground">Cash Flow · Company</span>
            <span className="ml-auto rounded-full bg-[hsl(var(--chart-received)/0.12)] px-2 py-0.5 text-[9px] font-semibold text-[hsl(var(--chart-received))]">
              Xero synced 2 min ago
            </span>
          </div>

          <div className="flex">
            {/* rail */}
            <div className="hidden w-9 flex-col items-center gap-2 border-r border-border bg-[hsl(var(--sidebar))] py-3 sm:flex" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={cn("h-5 w-5 rounded-md", i === 2 ? "bg-primary/15" : "bg-foreground/[0.06]")} />
              ))}
            </div>

            <div className="flex-1 p-3">
              {/* KPIs */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["Blended margin", "21.4%", "+1.2 pts vs budget", "text-[hsl(var(--chart-received))]"],
                  ["Committed costs", "$412,300", "across 6 live jobs", "text-muted-foreground"],
                  ["Matched to Xero", "94%", "3 bills need a job", "text-amber-600"],
                ].map(([k, v, sub, cls]) => (
                  <div key={k} className="rounded-lg border border-border bg-card p-2.5 shadow-[var(--shadow-card)]">
                    <p className="text-[8px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{k}</p>
                    <p className="mt-0.5 font-mono-nums text-[15px] font-semibold text-foreground">{v}</p>
                    <p className={cn("text-[8px]", cls)}>{sub}</p>
                  </div>
                ))}
              </div>

              <div className="mt-2 grid grid-cols-5 gap-2">
                {/* chart */}
                <div className="col-span-3 rounded-lg border border-border bg-card p-2.5 shadow-[var(--shadow-card)]">
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] font-semibold text-foreground">Cashflow — last 6 months</p>
                    <span className="flex items-center gap-2 text-[7px] text-muted-foreground">
                      <span className="flex items-center gap-1"><i className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-invoiced))]" />Invoiced</span>
                      <span className="flex items-center gap-1"><i className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-received))]" />Received</span>
                      <span className="flex items-center gap-1"><i className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-expenses))]" />Expenses</span>
                    </span>
                  </div>
                  <div className="mt-2 flex h-20 items-end gap-2 border-b border-border" aria-hidden="true">
                    {bars.map((set, i) => (
                      <div key={i} className="flex flex-1 items-end justify-center gap-[2px]">
                        <span className="w-full rounded-t-[2px] bg-[hsl(var(--chart-invoiced))]" style={{ height: `${set[0]}%` }} />
                        <span className="w-full rounded-t-[2px] bg-[hsl(var(--chart-received))]" style={{ height: `${set[1]}%` }} />
                        <span className="w-full rounded-t-[2px] bg-[hsl(var(--chart-expenses))]" style={{ height: `${set[2]}%` }} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-1 flex justify-between text-[7px] text-muted-foreground" aria-hidden="true">
                    {["Apr", "May", "Jun", "Jul", "Aug", "Sep"].map((m) => (
                      <span key={m}>{m}</span>
                    ))}
                  </div>
                </div>

                {/* margin guard */}
                <div className="col-span-2 rounded-lg border border-border bg-card p-2.5 shadow-[var(--shadow-card)]">
                  <p className="flex items-center gap-1 text-[9px] font-semibold text-foreground">
                    <Landmark className="h-3 w-3 text-primary" /> Margin guard
                  </p>
                  <div className="mt-2 space-y-1.5">
                    {[
                      ["Hawthorn Reno", 24, "text-[hsl(var(--chart-received))]"],
                      ["Brunswick Duplex", 19, "text-amber-600"],
                      ["Kew Extension", 22, "text-[hsl(var(--chart-received))]"],
                    ].map(([j, pct, cls]) => (
                      <div key={j}>
                        <div className="flex justify-between text-[8px]">
                          <span className="text-foreground">{j}</span>
                          <span className={cn("font-mono-nums font-semibold", cls)}>{pct}%</span>
                        </div>
                        <div className="mt-0.5 h-1 rounded-full bg-muted" aria-hidden="true">
                          <span className="block h-1 rounded-full bg-[hsl(var(--chart-net))]" style={{ width: `${pct * 3.5}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* table */}
              <div className="mt-2 overflow-hidden rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
                <div className="flex items-center justify-between border-b border-border bg-[hsl(210_22%_97%)] px-2.5 py-1.5">
                  <p className="flex items-center gap-1 text-[9px] font-semibold text-foreground">
                    <Sparkles className="h-3 w-3 text-primary" /> Recent cost matches
                  </p>
                  <span className="flex items-center gap-0.5 text-[8px] text-primary">
                    Open inbox <ArrowUpRight className="h-2.5 w-2.5" />
                  </span>
                </div>
                <div className="divide-y divide-border">
                  {[
                    ["Bunnings Trade", "PO-0142", "$3,840.00", "Matched"],
                    ["Reece Plumbing", "PO-0139", "$6,120.50", "Matched"],
                    ["Boral Concrete", "—", "$9,870.00", "Needs job"],
                  ].map(([sup, po, amt, st]) => (
                    <div key={sup} className="grid grid-cols-[1.4fr_0.8fr_0.9fr_0.8fr] items-center px-2.5 py-1.5 text-[8.5px]">
                      <span className="text-foreground">{sup}</span>
                      <span className="font-mono-nums text-muted-foreground">{po}</span>
                      <span className="font-mono-nums text-foreground">{amt}</span>
                      <span className={cn("justify-self-end rounded-full px-1.5 py-0.5 text-[7.5px] font-semibold", st === "Matched" ? "bg-[hsl(var(--chart-received)/0.12)] text-[hsl(var(--chart-received))]" : "bg-amber-400/90 text-stone-950")}>
                        {st}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* laptop base */}
      <div className="mx-auto h-2.5 w-[72%] rounded-b-[14px] border border-t-0 border-[rgba(11,42,74,0.14)] bg-[hsl(214_28%_95%)]" aria-hidden="true" />
    </div>
  );
};

/**
 * Hero composition: laptop behind, phone overlapping the laptop's lower-left so the
 * KPI row stays readable while the phone hangs ~90px below the laptop for depth.
 */
export const HeroMockups = ({ className }) => (
  <div className={cn("relative mx-auto w-full max-w-[720px] sm:pb-20 sm:pl-10 lg:pl-12", className)} data-testid="landing-hero-mockups">
    <LaptopMockup className="relative z-0 ml-auto" />
    <PhoneMockup className="absolute -left-2 bottom-0 z-10 hidden origin-bottom-left scale-[0.8] sm:block" />
    <div className="mt-6 flex justify-center sm:hidden">
      <PhoneMockup />
    </div>
  </div>
);
